import React, { useCallback, useEffect, useState } from "react";
import Button from "@material-ui/core/Button";
import {
  CoachReportRoom,
  listCoachReportRooms,
  unlinkCoachReportRoom,
} from "../../../../database/community/coachReportRooms";

type Props = {
  communityId: string;
  disabled?: boolean;
};

const callableErrorMessage = (error: any): string =>
  String(error?.message || "Could not reach the WhatsApp coach service.")
    .replace(/^.*?:\s*/, "")
    .trim();

// The worker stores the JID and, for newer links, the group name. Show the
// name when there is one, and a short JID tail otherwise so two rooms in the
// same list stay tellable apart.
const roomLabel = (room: CoachReportRoom): string =>
  room.groupName || `WhatsApp group ${room.groupJid.split("@")[0].slice(-6)}`;

const roleLabel = (role: CoachReportRoom["role"]): string =>
  role === "report_only"
    ? "Reports only. The group belongs to another community."
    : "Linked group. Reports and signup alerts go here.";

export default function CoachReportRoomsSettings({
  communityId,
  disabled = false,
}: Props) {
  const [rooms, setRooms] = useState<CoachReportRoom[]>([]);
  // The worker's own count. An older worker reports a count but cannot list
  // the rooms yet, and the two numbers must not be confused.
  const [roomCount, setRoomCount] = useState(0);
  const [loading, setLoading] = useState(Boolean(communityId));
  const [confirming, setConfirming] = useState("");
  const [unlinking, setUnlinking] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(
    async (): Promise<{ details: CoachReportRoom[]; count: number }> => {
      const result = await listCoachReportRooms(communityId);
      return {
        details: Array.isArray(result?.roomDetails) ? result.roomDetails : [],
        count: Number(result?.rooms) || 0,
      };
    },
    [communityId]
  );

  useEffect(() => {
    let active = true;
    setMessage("");
    setError("");
    setConfirming("");
    if (!communityId) {
      setRooms([]);
      setRoomCount(0);
      setLoading(false);
      return () => {
        active = false;
      };
    }
    setLoading(true);
    load()
      .then((loaded) => {
        if (!active) return;
        setRooms(loaded.details);
        setRoomCount(loaded.count);
      })
      .catch((loadError) => {
        if (active) setError(callableErrorMessage(loadError));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [communityId, load]);

  const unlink = async (room: CoachReportRoom) => {
    if (!communityId || unlinking) return;
    setUnlinking(room.groupJid);
    setMessage("");
    setError("");
    try {
      const result = await unlinkCoachReportRoom({
        communityId,
        groupJid: room.groupJid,
      });
      const acted = Array.isArray(result?.rooms) ? result.rooms : [];
      const action = acted[0]?.action;
      setMessage(
        action === "report_removed"
          ? `${roomLabel(room)} no longer receives this community's reports.`
          : `${roomLabel(room)} is unlinked. It gets no more reports or alerts.`
      );
      setConfirming("");
      // The unlink already went through. A failed refresh is a separate
      // problem, so it shows next to the success line instead of replacing it.
      try {
        const loaded = await load();
        setRooms(loaded.details);
        setRoomCount(loaded.count);
      } catch (refreshError) {
        setError(
          `The room was unlinked, but the list could not be refreshed: ${callableErrorMessage(
            refreshError
          )}`
        );
      }
    } catch (unlinkError) {
      setError(callableErrorMessage(unlinkError));
    } finally {
      setUnlinking("");
    }
  };

  if (!communityId) {
    return (
      <div style={{ fontSize: 13, color: "#666", marginTop: 12 }}>
        Create the community before linking a WhatsApp coach room.
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: 16,
        padding: 16,
        border: "1px solid #e1e1e1",
        borderRadius: 8,
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
        WhatsApp coach rooms
      </div>
      <div style={{ fontSize: 13, color: "#666", marginBottom: 12 }}>
        The WhatsApp groups that get this community's weekly report and signup
        alerts. A group is linked from inside WhatsApp. Unlink it here to stop
        the delivery and revoke the group's access.
      </div>

      {loading ? (
        <div style={{ fontSize: 13, color: "#666" }}>Loading rooms…</div>
      ) : null}

      {!loading && !error && !rooms.length ? (
        <div style={{ fontSize: 13, color: "#666" }}>
          {roomCount > 0
            ? `The WhatsApp service reports ${roomCount} linked ${
                roomCount === 1 ? "group" : "groups"
              } but cannot list them yet. Try again after the next service update.`
            : "No WhatsApp group is linked to this community yet."}
        </div>
      ) : null}

      {!loading
        ? rooms.map((room) => (
            <div
              key={room.groupJid}
              style={{
                borderTop: "1px solid #f0f0f0",
                paddingTop: 12,
                marginTop: 12,
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 500 }}>
                {roomLabel(room)}
              </div>
              <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                {roleLabel(room.role)}
              </div>
              {!disabled && confirming === room.groupJid ? (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 13, color: "#666" }}>
                    {room.role === "report_only"
                      ? "This group keeps working for its own community. It stops receiving this community's reports."
                      : "The group stops receiving reports and alerts, and its access is revoked. Relinking is done from inside WhatsApp."}
                  </div>
                  <Button
                    type="button"
                    variant="contained"
                    color="secondary"
                    disabled={Boolean(unlinking)}
                    onClick={() => unlink(room)}
                    style={{ marginTop: 8, marginRight: 8 }}
                  >
                    {unlinking === room.groupJid ? "Unlinking…" : "Yes, unlink"}
                  </Button>
                  <Button
                    type="button"
                    variant="outlined"
                    disabled={Boolean(unlinking)}
                    onClick={() => setConfirming("")}
                    style={{ marginTop: 8 }}
                  >
                    Cancel
                  </Button>
                </div>
              ) : null}
              {!disabled && confirming !== room.groupJid ? (
                <Button
                  type="button"
                  variant="outlined"
                  disabled={Boolean(unlinking)}
                  onClick={() => {
                    setMessage("");
                    setError("");
                    setConfirming(room.groupJid);
                  }}
                  style={{ marginTop: 8 }}
                >
                  Unlink
                </Button>
              ) : null}
            </div>
          ))
        : null}

      {message ? (
        <div style={{ fontSize: 13, color: "#2e7d32", marginTop: 12 }}>
          {message}
        </div>
      ) : null}
      {error ? (
        <div style={{ fontSize: 13, color: "#c62828", marginTop: 12 }}>
          {error}
        </div>
      ) : null}
    </div>
  );
}
